import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Inbox, Mail, Briefcase, RefreshCw, Search, Download, Loader2, ExternalLink, Lock, LogOut, Bell, Check } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  getContacts, getNewsletter, getApplications, getLeadsSummary,
  getNotifications, markAllNotificationsRead, adminLogin, adminVerify, ADMIN_TOKEN_KEY,
} from "@/lib/api";
import { cn } from "@/lib/utils";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function toCSV(rows, headers) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(esc).join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => esc(r[h])).join(",")));
  return lines.join("\n");
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------- Login gate ---------- */
function LoginGate({ onSuccess }) {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    setLoading(true);
    try {
      const res = await adminLogin(passcode.trim());
      localStorage.setItem(ADMIN_TOKEN_KEY, res.token);
      toast.success("Welcome back!");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Incorrect passcode" : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="grid min-h-[70vh] place-items-center px-5 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm rounded-3xl border border-border bg-card p-8 shadow-xl" data-testid="admin-login">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary"><Lock className="h-6 w-6" /></span>
        <h1 className="mt-6 font-display text-2xl font-semibold tracking-tight">Team access</h1>
        <p className="mt-2 text-sm text-muted-foreground">Enter the shared passcode to open the Leads Dashboard.</p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <Input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            data-testid="admin-passcode"
            autoFocus
          />
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock Dashboard"}
          </button>
        </form>
      </motion.div>
    </section>
  );
}

function StatCard({ icon: I, label, value, active }) {
  return (
    <div className={cn("rounded-2xl border p-5", active ? "border-primary bg-primary/5" : "border-border bg-card")}>
      <div className="flex items-center justify-between">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary"><I className="h-5 w-5" /></span>
        <span className="font-display text-3xl font-semibold">{value}</span>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function DataTable({ columns, rows, empty }) {
  if (!rows.length) {
    return <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="dashboard-empty">{empty}</div>;
  }
  return (
    <div className="overflow-x-auto rounded-2xl border border-border">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-border bg-muted/50">
          <tr>{columns.map((c) => <th key={c.key} className="whitespace-nowrap px-4 py-3 font-semibold text-muted-foreground">{c.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, i) => (
            <tr key={r.id || i} className="transition-colors hover:bg-muted/40" data-testid="dashboard-row">
              {columns.map((c) => <td key={c.key} className="px-4 py-3 align-top">{c.render ? c.render(r) : (r[c.key] || "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Notifications panel ---------- */
function NotificationsBell({ items, unread, onMarkRead }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="notifications-bell" aria-label="Notifications" className="relative grid h-11 w-11 place-items-center rounded-full border border-border bg-card transition-colors hover:border-primary/50">
        <Bell className="h-5 w-5" />
        {unread > 0 && <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white" data-testid="notifications-badge">{unread}</span>}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-border bg-popover shadow-2xl" data-testid="notifications-panel">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="text-sm font-semibold">Notifications</p>
              {unread > 0 && (
                <button onClick={onMarkRead} data-testid="notifications-mark-read" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                  <Check className="h-3.5 w-3.5" /> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet.</p>
              ) : (
                items.slice(0, 30).map((n) => (
                  <div key={n.id} className={cn("border-b border-border px-4 py-3 last:border-0", !n.read && "bg-primary/5")}>
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                      <div className={cn(n.read && "pl-4")}>
                        <p className="text-sm font-medium">{n.title}</p>
                        <p className="text-xs text-muted-foreground">{n.body}</p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground/70">{fmt(n.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Dashboard ---------- */
function DashboardInner({ onLogout }) {
  const [summary, setSummary] = useState({ contacts: 0, newsletter: 0, applications: 0 });
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [apps, setApps] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("contacts");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, n, a, no] = await Promise.all([getLeadsSummary(), getContacts(), getNewsletter(), getApplications(), getNotifications()]);
      setSummary(s);
      setContacts(c.items || []);
      setSubs(n.items || []);
      setApps(a.items || []);
      setNotifs(no.items || []);
      setUnread(no.unread || 0);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const markRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifs((ns) => ns.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const match = (obj) => !q.trim() || JSON.stringify(obj).toLowerCase().includes(q.toLowerCase());
  const fContacts = contacts.filter(match);
  const fSubs = subs.filter(match);
  const fApps = apps.filter(match);

  const exportCurrent = () => {
    if (tab === "contacts") download("contacts.csv", toCSV(fContacts, ["created_at", "name", "company", "email", "phone", "service", "budget", "message", "source", "status"]));
    else if (tab === "newsletter") download("newsletter.csv", toCSV(fSubs, ["created_at", "email"]));
    else download("applications.csv", toCSV(fApps, ["created_at", "name", "email", "phone", "role", "portfolio", "message", "status"]));
    toast.success("Export started");
  };

  return (
    <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-3 flex items-center gap-2">
            <span className="h-px w-8 bg-primary" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Admin</span>
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Leads Dashboard</h1>
          <p className="mt-2 text-muted-foreground">Every contact enquiry, newsletter subscriber and job application in one place.</p>
        </div>
        <div className="flex items-center gap-3">
          <NotificationsBell items={notifs} unread={unread} onMarkRead={markRead} />
          <button onClick={load} data-testid="dashboard-refresh" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium transition-colors hover:border-primary/50">
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /> Refresh
          </button>
          <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium transition-colors hover:border-destructive/50 hover:text-destructive">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <StatCard icon={Inbox} label="Contact enquiries" value={summary.contacts} active={tab === "contacts"} />
        <StatCard icon={Mail} label="Newsletter subscribers" value={summary.newsletter} active={tab === "newsletter"} />
        <StatCard icon={Briefcase} label="Job applications" value={summary.applications} active={tab === "applications"} />
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search across all leads..." className="pl-9" data-testid="dashboard-search" />
        </div>
        <button onClick={exportCurrent} data-testid="dashboard-export" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {loading ? (
        <div className="mt-16 flex items-center justify-center gap-3 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading leads...</div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="dashboard-tabs">
              <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({fContacts.length})</TabsTrigger>
              <TabsTrigger value="newsletter" data-testid="tab-newsletter">Newsletter ({fSubs.length})</TabsTrigger>
              <TabsTrigger value="applications" data-testid="tab-applications">Applications ({fApps.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="contacts" className="mt-5">
              <DataTable
                empty="No contact enquiries yet."
                rows={fContacts}
                columns={[
                  { key: "created_at", label: "Date", render: (r) => <span className="whitespace-nowrap text-muted-foreground">{fmt(r.created_at)}</span> },
                  { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
                  { key: "source", label: "Source", render: (r) => <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{r.source || "Website"}</span> },
                  { key: "email", label: "Email", render: (r) => <a href={`mailto:${r.email}`} className="text-primary hover:underline">{r.email}</a> },
                  { key: "phone", label: "Phone" },
                  { key: "service", label: "Service" },
                  { key: "budget", label: "Budget" },
                  { key: "message", label: "Message", render: (r) => <span className="block max-w-xs text-muted-foreground">{r.message}</span> },
                ]}
              />
            </TabsContent>

            <TabsContent value="newsletter" className="mt-5">
              <DataTable
                empty="No newsletter subscribers yet."
                rows={fSubs}
                columns={[
                  { key: "created_at", label: "Date", render: (r) => <span className="whitespace-nowrap text-muted-foreground">{fmt(r.created_at)}</span> },
                  { key: "email", label: "Email", render: (r) => <a href={`mailto:${r.email}`} className="font-medium text-primary hover:underline">{r.email}</a> },
                ]}
              />
            </TabsContent>

            <TabsContent value="applications" className="mt-5">
              <DataTable
                empty="No job applications yet."
                rows={fApps}
                columns={[
                  { key: "created_at", label: "Date", render: (r) => <span className="whitespace-nowrap text-muted-foreground">{fmt(r.created_at)}</span> },
                  { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
                  { key: "role", label: "Role" },
                  { key: "email", label: "Email", render: (r) => <a href={`mailto:${r.email}`} className="text-primary hover:underline">{r.email}</a> },
                  { key: "phone", label: "Phone" },
                  { key: "portfolio", label: "Portfolio", render: (r) => r.portfolio ? <a href={r.portfolio} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">Link <ExternalLink className="h-3 w-3" /></a> : "—" },
                  { key: "message", label: "Note", render: (r) => <span className="block max-w-xs text-muted-foreground">{r.message || "—"}</span> },
                ]}
              />
            </TabsContent>
          </Tabs>
        </motion.div>
      )}
    </section>
  );
}

export default function Dashboard() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    adminVerify().then(() => setAuthed(true)).catch(() => {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setAuthed(false);
    });
  }, []);

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthed(false);
  };

  return (
    <>
      <SEO title="Leads Dashboard" description="Internal leads dashboard for NeuroTriq TechLabs." path="/admin" />
      {authed === null ? (
        <div className="grid min-h-[70vh] place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : authed ? (
        <DashboardInner onLogout={logout} />
      ) : (
        <LoginGate onSuccess={() => setAuthed(true)} />
      )}
    </>
  );
}
